import React, { useEffect, useState } from "react";
import { Alert, Card, CardBody, Col, Row, Spinner, Progress } from "reactstrap";
import SimpleBar from "simplebar-react";
import { getBarcodeGenerationStatistics } from "../../services/ValitanApiService";

const BarcodeGenerationStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getBarcodeGenerationStatistics();
        if (!isMounted) return;
        setStats(response?.data?.data || null);
      } catch (err) {
        if (!isMounted) return;
        console.error("Error fetching barcode stats:", err);
        setError("Failed to load barcode generation statistics.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();
    return () => {
      isMounted = false;
    };
  }, []);

  const getProgressColor = (percent) => {
    if (percent >= 100) return "success";
    if (percent >= 50) return "primary";
    if (percent > 0) return "warning";
    return "danger";
  };

  return (
    <section className="mb-4">
      <div className="mb-3 d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <div>
          <h2 className="dashboard-section-title mb-1">Barcode Generation</h2>
          <p className="dashboard-section-copy">
            Overview of barcode generation progress across all categories.
          </p>
        </div>
      </div>
      
      <Card className="dashboard-shell-card p-4">
        <CardBody className="p-0">
          {loading ? (
            <div className="py-4 text-center">
              <Spinner color="primary" />
            </div>
          ) : error ? (
            <Alert color="danger" className="mb-0">
              {error}
            </Alert>
          ) : !stats ? (
            <Alert color="warning" className="mb-0">
              No barcode data available.
            </Alert>
          ) : (
            <Row className="g-4">
              <Col lg={4}>
                <div className="p-3 border rounded h-100 bg-light">
                  <h5 className="mb-3 fw-bold text-muted text-uppercase" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                    Generation Summary
                  </h5>
                  <div className="mb-4">
                    <h3 className="mb-1">{(stats.total_generated_barcodes || stats.totalGeneratedBarcodes || 0).toLocaleString()} <span className="text-muted fs-5 fw-normal">/ {(stats.total_target_barcodes || stats.totalTargetBarcodes || 0).toLocaleString()}</span></h3>
                    <p className="text-muted mb-2">Total Generated vs Target</p>
                    <Progress 
                      value={((stats.total_target_barcodes || stats.totalTargetBarcodes || 0) > 0) ? ((stats.total_generated_barcodes || stats.totalGeneratedBarcodes || 0) / (stats.total_target_barcodes || stats.totalTargetBarcodes || 0)) * 100 : 0} 
                      color={getProgressColor(((stats.total_target_barcodes || stats.totalTargetBarcodes || 0) > 0) ? ((stats.total_generated_barcodes || stats.totalGeneratedBarcodes || 0) / (stats.total_target_barcodes || stats.totalTargetBarcodes || 0)) * 100 : 0)} 
                      style={{ height: '8px' }} 
                    />
                  </div>
                  
                  <div className="d-flex align-items-center mb-3">
                    <div className="flex-shrink-0 me-3">
                      <div className="avatar-sm">
                        <span className="avatar-title bg-success bg-opacity-10 text-success rounded-circle fs-4" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="ri-barcode-line"></i>
                        </span>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">Generated</p>
                      <h5 className="mb-0">{(stats.total_generated_barcodes || stats.totalGeneratedBarcodes || 0).toLocaleString()}</h5>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-3">
                      <div className="avatar-sm">
                        <span className="avatar-title bg-warning bg-opacity-10 text-warning rounded-circle fs-4" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="ri-error-warning-line"></i>
                        </span>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">Missing</p>
                      <h5 className="mb-0">{(stats.total_missing_barcodes || stats.totalMissingBarcodes || 0).toLocaleString()}</h5>
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col lg={8}>
                <h5 className="mb-3 fw-bold text-muted text-uppercase" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                  Category Breakdown
                </h5>
                <SimpleBar style={{ maxHeight: '250px' }}>
                  <div className="table-responsive pe-2">
                    <table className="table table-borderless align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Category</th>
                          <th className="text-end">Target</th>
                          <th className="text-end">Generated</th>
                          <th className="text-end">Missing</th>
                          <th className="text-center" style={{ width: '120px' }}>Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stats.category_breakdown || stats.categoryBreakdown || []).map((cat) => {
                          const percent = cat.target > 0 ? (cat.generated / cat.target) * 100 : 0;
                          const catCode = cat.category_code || cat.categoryCode;
                          const catName = cat.category_name || cat.categoryName;
                          return (
                            <tr key={catCode} className="border-bottom border-light">
                              <td>
                                <h6 className="mb-0 text-truncate" style={{ maxWidth: '200px' }} title={catName}>
                                  {catName} <span className="text-muted">({catCode})</span>
                                </h6>
                              </td>
                              <td className="text-end fw-semibold">{(cat.target || 0).toLocaleString()}</td>
                              <td className="text-end text-success fw-semibold">{(cat.generated || 0).toLocaleString()}</td>
                              <td className="text-end text-danger fw-semibold">{(cat.missing || 0).toLocaleString()}</td>
                              <td className="text-center">
                                <div className="d-flex flex-column align-items-center">
                                  <span className="small text-muted mb-1">{percent.toFixed(0)}%</span>
                                  <Progress value={percent} color={getProgressColor(percent)} style={{ height: '5px', width: '100%' }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {(!(stats.category_breakdown || stats.categoryBreakdown) || (stats.category_breakdown || stats.categoryBreakdown).length === 0) && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-3">No categories available</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </SimpleBar>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
    </section>
  );
};

export default BarcodeGenerationStats;
